import React, { useRef, useState } from "react";
import { Plus, Send, AlertTriangle, Car } from "lucide-react";
import DriverMap, { driverIsLive } from "../components/DriverMap";
import { useToast } from "../components/Toast";
import {
  useGetDriversQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useGetDriverConversationQuery,
  useReplyToDriverMutation,
  useGetDriverAlertsQuery,
  useResolveDriverAlertMutation,
} from "../store/api/driversApi";
const empty = { name: "", email: "", phone: "", vehicle: "", password: "" };
export default function Drivers() {
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(empty);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const attempt = useRef(null);
  const { data, error, isLoading } = useGetDriversQuery(undefined, {
    pollingInterval: 2000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: alertsData, error: alertsError } = useGetDriverAlertsQuery(
    undefined,
    { pollingInterval: 2000 },
  );
  const {
    data: chat,
    error: chatError,
    isFetching: chatLoading,
  } = useGetDriverConversationQuery(selectedId, {
    skip: !selectedId,
    pollingInterval: 2000,
  });
  const [create, { isLoading: creating }] = useCreateDriverMutation();
  const [update, { isLoading: updating }] = useUpdateDriverMutation();
  const [reply, { isLoading: sending }] = useReplyToDriverMutation();
  const [resolve, { isLoading: resolving }] = useResolveDriverAlertMutation();
  const drivers = data?.data || [];
  const selected = drivers.find((driver) => driver.id === selectedId);
  const fail = (error) =>
    toast.error(
      error?.data?.error?.message ||
        "Request failed. Check your connection and retry.",
    );
  const select = (id) => {
    setSelectedId(id);
    setDraft("");
    setResetPassword("");
    attempt.current = null;
  };
  const createAccount = async (event) => {
    event.preventDefault();
    try {
      const result = await create(form).unwrap();
      setForm(empty);
      setShowCreate(false);
      if (result.data?.credentialsEmailSent) {
        toast.success(
          "Driver created. Login credentials emailed to the driver.",
        );
      } else {
        toast.error(
          "Driver created, but the credentials email could not be sent. Share the login credentials with the driver securely.",
        );
      }
    } catch (error) {
      fail(error);
    }
  };
  const updateAccount = async (body) => {
    try {
      await update({ id: selectedId, ...body }).unwrap();
      setResetPassword("");
      toast.success("Driver account updated.");
    } catch (error) {
      fail(error);
    }
  };
  const send = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    const key = `${selectedId}:${text}`;
    if (attempt.current?.key !== key)
      attempt.current = {
        key,
        clientId:
          window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
    try {
      await reply({
        id: selectedId,
        kind: "message",
        text,
        clientId: attempt.current.clientId,
      }).unwrap();
      setDraft("");
      attempt.current = null;
    } catch (error) {
      fail(error);
    }
  };
  return (
    <div className="p-6 font-manrope-regular space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-manrope-bold">Drivers</h1>
          <p className="text-gray-500 text-sm mt-1">
            Accounts, live locations, conversations and vehicle alerts.
          </p>
        </div>
        <button
          className="flex gap-2 items-center bg-[#0d5c5c] text-white px-4 py-2 rounded-lg"
          onClick={() => setShowCreate(!showCreate)}
        >
          <Plus size={16} />
          Create driver
        </button>
      </div>
      {showCreate && (
        <form
          onSubmit={createAccount}
          className="bg-white border rounded-xl p-5 space-y-4"
        >
          <h2 className="font-manrope-bold">New driver account</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.keys(empty).map((key) => (
              <label key={key} className="text-sm capitalize">
                {key === "vehicle" ? "Vehicle / registration" : key}
                <input
                  aria-label={key}
                  className="block border rounded-lg px-3 py-2 w-full mt-1"
                  type={
                    key === "password"
                      ? "password"
                      : key === "email"
                        ? "email"
                        : "text"
                  }
                  required={["name", "email", "password"].includes(key)}
                  minLength={key === "password" ? 8 : undefined}
                  maxLength={
                    key === "password" ? 128 : key === "email" ? 254 : 100
                  }
                  autoComplete={key === "password" ? "new-password" : "off"}
                  value={form[key]}
                  onChange={(event) =>
                    setForm({ ...form, [key]: event.target.value })
                  }
                />
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            The driver signs into the app using this email and password.
          </p>
          <button
            disabled={creating}
            className="bg-[#0d5c5c] text-white px-4 py-2 rounded-lg"
          >
            {creating ? "Creating…" : "Create account"}
          </button>
          <button
            type="button"
            className="ml-3 text-gray-600"
            onClick={() => {
              setShowCreate(false);
              setForm(empty);
            }}
          >
            Cancel
          </button>
        </form>
      )}
      {!!error && (
        <p role="alert" className="text-red-700">
          Could not refresh drivers. Showing the last available data.
        </p>
      )}
      <div className="grid xl:grid-cols-[1fr_340px] gap-5">
        <section className="bg-white border rounded-xl p-4">
          <h2 className="font-manrope-bold mb-3">Driver locations</h2>
          <DriverMap
            drivers={drivers}
            selectedId={selectedId}
            onSelect={select}
          />
        </section>
        <section className="bg-white border rounded-xl p-4 max-h-[490px] overflow-y-auto">
          <h2 className="font-manrope-bold mb-3">Driver accounts</h2>
          {isLoading && <p>Loading…</p>}
          {!isLoading && !drivers.length && (
            <p className="text-gray-500">
              Create your first driver account to start tracking.
            </p>
          )}
          {drivers.map((driver) => (
            <button
              key={driver.id}
              className={`block w-full text-left p-3 rounded-lg mb-2 border ${driver.id === selectedId ? "border-teal-700 bg-teal-50" : "border-gray-100"}`}
              onClick={() => select(driver.id)}
            >
              <span className="font-manrope-bold flex gap-2 items-center">
                <Car size={16} />
                {driver.name}
              </span>
              <span className="block text-sm text-gray-500">
                {driver.vehicle || "No vehicle"} ·{" "}
                {driver.isActive === false
                  ? "Disabled"
                  : driverIsLive(driver)
                    ? "Live"
                    : "Offline"}
              </span>
              <span className="block text-xs mt-1 text-gray-500">
                {driver.lastLocation
                  ? `${Number(driver.lastLocation.lat).toFixed(5)}, ${Number(driver.lastLocation.lng).toFixed(5)} · ${new Date(driver.lastLocation.ts).toLocaleString()}`
                  : "No location received yet"}
              </span>
            </button>
          ))}
        </section>
      </div>
      <section className="bg-white border rounded-xl p-5">
        <h2 className="font-manrope-bold flex gap-2 items-center mb-3">
          <AlertTriangle className="text-red-600" size={18} />
          Open vehicle alerts
        </h2>
        {alertsError && (
          <p role="alert" className="text-red-700">
            Could not refresh alerts. Retrying…
          </p>
        )}
        {!alertsData?.data?.length && (
          <p className="text-gray-500 text-sm">No open alerts.</p>
        )}
        {alertsData?.data?.map((alert) => (
          <div
            key={alert.id}
            className="p-4 bg-red-50 border border-red-100 rounded-lg mt-3"
          >
            <button
              className="font-manrope-bold text-red-800"
              onClick={() => select(alert.driverId)}
            >
              {drivers.find((driver) => driver.id === alert.driverId)?.name ||
                "Driver"}{" "}
              · {alert.category}
            </button>
            <p className="mt-1 whitespace-pre-wrap">{alert.text}</p>
            <p className="text-xs text-gray-500 mt-2">
              {new Date(alert.createdAt).toLocaleString()}
              {alert.location
                ? ` · ${alert.location.lat.toFixed(5)}, ${alert.location.lng.toFixed(5)}`
                : " · Location unavailable"}
            </p>
            <button
              disabled={resolving}
              className="mt-3 text-sm underline text-red-800"
              onClick={async () => {
                try {
                  await resolve(alert.id).unwrap();
                  toast.success("Alert resolved.");
                } catch (error) {
                  fail(error);
                }
              }}
            >
              Mark resolved
            </button>
          </div>
        ))}
      </section>
      {selected && (
        <section className="bg-white border rounded-xl p-5">
          <div className="flex justify-between gap-4">
            <div>
              <h2 className="font-manrope-bold">Chat with {selected.name}</h2>
              <p className="text-gray-500 text-sm">
                {selected.email} · {selected.phone || "No phone"}
              </p>
            </div>
            <button
              disabled={updating}
              onClick={() =>
                updateAccount({ isActive: selected.isActive === false })
              }
              className="text-sm text-red-700"
            >
              {selected.isActive === false
                ? "Enable account"
                : "Disable account"}
            </button>
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void updateAccount({ password: resetPassword });
            }}
            className="flex gap-2 mt-4"
          >
            <input
              aria-label="New driver password"
              type="password"
              minLength={8}
              maxLength={128}
              required
              placeholder="New password"
              autoComplete="new-password"
              value={resetPassword}
              onChange={(event) => setResetPassword(event.target.value)}
              className="border rounded-lg p-2"
            />
            <button
              disabled={updating || !resetPassword}
              className="text-sm text-teal-800"
            >
              Reset password
            </button>
          </form>
          {chatError && (
            <p role="alert" className="text-red-700 mt-3">
              Could not load this conversation.
            </p>
          )}
          <div className="max-h-96 overflow-y-auto space-y-3 my-5">
            {chatLoading && !chat && <p>Loading messages…</p>}
            {chat?.data
              ?.filter((message) => message.driverId === selectedId)
              .map((message) => (
                <div
                  key={message.id}
                  className={`p-3 rounded-lg ${message.kind === "alert" ? "bg-red-50" : message.senderRole === "admin" ? "bg-teal-50" : "bg-gray-50"}`}
                >
                  <p className="text-xs text-gray-500">
                    {message.senderRole === "admin" ? "Admin" : selected.name}
                    {message.kind === "alert"
                      ? ` · ${message.resolvedAt ? "Resolved alert" : "Vehicle alert"}`
                      : ""}{" "}
                    · {new Date(message.createdAt).toLocaleString()}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap">{message.text}</p>
                </div>
              ))}
          </div>
          <form onSubmit={send} className="flex gap-3">
            <textarea
              aria-label="Reply to driver"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              maxLength={2000}
              placeholder="Reply to driver…"
              className="border rounded-lg p-3 flex-1"
              required
              disabled={selected.isActive === false}
            />
            <button
              disabled={sending || !draft.trim() || selected.isActive === false}
              className="bg-[#0d5c5c] text-white px-5 rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              <Send size={16} />
              {sending ? "Sending…" : "Send"}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
